import { useCallback } from 'react';

import { useQuery, useQueryClient } from 'react-query';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';
import { OcrTextLine } from '@/new/components/pdf-viewer/text-matching';

// ---- Raw shape of the per-page text JSON stored in S3 ----

interface RawBoundingBox {
  Width: number;
  Height: number;
  Left: number;
  Top: number;
}

interface RawWord {
  Text: string;
  BoundingBox: RawBoundingBox;
}

interface RawLine {
  Id?: string;
  Text: string;
  BoundingBox: RawBoundingBox;
  words?: RawWord[];
}

interface PagesTextResponse {
  pages: { pageNumber: number; content: RawLine[] }[];
}

const PAGE_TEXT_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  staleTime: 590 * 1000,
  cacheTime: 590 * 1000,
} as const;

const EMPTY_PAGES_TEXT: Record<number, OcrTextLine[]> = {};

const normaliseBox = (box: RawBoundingBox) => ({
  left: box.Left,
  top: box.Top,
  width: box.Width,
  height: box.Height,
});

const parsePageText = (raw: RawLine[]): OcrTextLine[] =>
  raw
    .filter((line) => !!line?.Text && !!line.BoundingBox)
    .map((line) => ({
      text: line.Text,
      ...normaliseBox(line.BoundingBox),
      words: line.words
        ?.filter((word) => !!word?.Text && !!word.BoundingBox)
        .map((word) => ({ text: word.Text, ...normaliseBox(word.BoundingBox) })),
    }));

const pageTextQueryKey = (matterId: string | undefined, docId: string | undefined, pageIndex: number) =>
  [QueryEntity.DocumentPageText, { matterId, docId, pageIndex }] as const;

/**
 * Fetch the extracted text for one or more pages.
 * Returns a map of pageIndex -> lines (0-based).
 */
const fetchPagesText = async (
  matterId: string | undefined,
  docId: string | undefined,
  requestInit: RequestInit,
): Promise<Record<number, OcrTextLine[]>> => {
  const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/docs/${docId}/page-texts`, requestInit);

  if (!response.ok) throw new Error('Fetching page texts failed');

  const { pages } = (await response.json()) as PagesTextResponse;

  const byPage: Record<number, OcrTextLine[]> = {};
  pages.forEach(({ pageNumber, content }) => {
    byPage[pageNumber] = Array.isArray(content) ? parsePageText(content) : [];
  });
  return byPage;
};

export const useGetDocPageText = (matterId: string | undefined, docId: string | undefined, pageIndex: number) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<OcrTextLine[]>(
    pageTextQueryKey(matterId, docId, pageIndex),
    async () => {
      const byPage = await fetchPagesText(
        matterId,
        docId,
        getFetchConfig({ method: FetchMethod.POST, data: { pageNumbers: [pageIndex] } }),
      );
      return byPage[pageIndex] ?? [];
    },
    {
      enabled: !!matterId && !!docId && pageIndex >= 0,
      ...PAGE_TEXT_QUERY_OPTIONS,
    },
  );
};

/**
 * Returns an imperative `fetchPage(pageIndex)` for fetching a single page's text
 * on demand, rather than a hook
 */
export const useLazyFetchDocPageText = (matterId: string | undefined, docId: string | undefined) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useCallback(
    (pageIndex: number): Promise<OcrTextLine[]> =>
      queryClient.fetchQuery(
        pageTextQueryKey(matterId, docId, pageIndex),
        async () => {
          const byPage = await fetchPagesText(
            matterId,
            docId,
            getFetchConfig({ method: FetchMethod.POST, data: { pageNumbers: [pageIndex] } }),
          );
          return byPage[pageIndex] ?? [];
        },
        PAGE_TEXT_QUERY_OPTIONS,
      ),
    [queryClient, matterId, docId, getFetchConfig],
  );
};

/**
 * Fetch extracted text for several pages in a single request.
 *
 * The fetched pages are seeded into the per-page query cache, so when one of
 * them later scrolls into view its {@link useGetDocPageText} read is served
 * from cache instead of issuing another request.
 */
export const useBatchGetDocPagesText = (
  matterId: string | undefined,
  docId: string | undefined,
  pageIndices: number[],
): { pagesText: Record<number, OcrTextLine[]>; isFetching: boolean } => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  // Stable key regardless of the order the page indices arrive in.
  const sortedPageIndices = [...pageIndices].sort((a, b) => a - b);

  const { data, isFetching } = useQuery<Record<number, OcrTextLine[]>>(
    [QueryEntity.DocumentPageText, 'Batch', { matterId, docId, pages: sortedPageIndices.join(',') }],
    () =>
      fetchPagesText(
        matterId,
        docId,
        getFetchConfig({ method: FetchMethod.POST, data: { pageNumbers: sortedPageIndices } }),
      ),
    {
      enabled: !!matterId && !!docId && sortedPageIndices.length > 0,
      ...PAGE_TEXT_QUERY_OPTIONS,
      onSuccess: (byPage) => {
        // Warm the per-page cache so visible-page renders reuse these results.
        Object.entries(byPage).forEach(([pageIndex, lines]) => {
          queryClient.setQueryData(pageTextQueryKey(matterId, docId, Number(pageIndex)), lines);
        });
      },
    },
  );

  return { pagesText: data ?? EMPTY_PAGES_TEXT, isFetching };
};
